import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import StartupCard from "../../components/marketplace/StartupCard";
import { Pill } from "../../components/common/Primitives";
import { Sparkles, ShieldCheck, Filter, X, Search, RotateCcw, Compass } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const CHIP_LIMIT = 4;

/**
 * InvestorMarketplace — filter drawer + URL-synced state
 * -----------------------------------------------------
 * All filters live in the querystring so the Concentration Alert on the
 * dashboard can deep-link investors straight into a diversified view.
 *
 * Recognised params:
 *   sectors, sectors_exclude, stages, funding_types  (all csv)
 *   city, search                                     (strings)
 *   min_ask, max_ask, min_trust, min_completeness    (numbers)
 *   only_featured                                    (bool)
 */
export default function InvestorMarketplace() {
  const [params, setParams] = useSearchParams();
  const [startups, setStartups] = useState([]);
  const [facets, setFacets] = useState({ sectors: [], stages: [], cities: [], funding_types: [] });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Read filters from URL — always the single source of truth.
  const filters = useMemo(() => ({
    search: params.get("search") || "",
    sectors: csv(params.get("sectors")),
    sectors_exclude: csv(params.get("sectors_exclude")),
    stages: csv(params.get("stages")),
    funding_types: csv(params.get("funding_types")),
    city: params.get("city") || "",
    min_ask: params.get("min_ask") || "",
    max_ask: params.get("max_ask") || "",
    min_trust: params.get("min_trust") || "",
    min_completeness: params.get("min_completeness") || "",
    only_featured: params.get("only_featured") === "1",
    sort: params.get("sort") || "match_score",
  }), [params]);

  useEffect(() => {
    api.get("/marketplace/filters").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (filters.sectors.length) p.sectors = filters.sectors.join(",");
    if (filters.sectors_exclude.length) p.sectors_exclude = filters.sectors_exclude.join(",");
    if (filters.stages.length) p.stages = filters.stages.join(",");
    if (filters.funding_types.length) p.funding_types = filters.funding_types.join(",");
    if (filters.city) p.city = filters.city;
    if (filters.min_ask) p.min_ask = filters.min_ask;
    if (filters.max_ask) p.max_ask = filters.max_ask;
    if (filters.min_trust) p.min_trust = filters.min_trust;
    if (filters.min_completeness) p.min_completeness = filters.min_completeness;
    if (filters.only_featured) p.only_featured = "1";
    if (filters.search) p.search = filters.search;
    if (filters.sort && filters.sort !== "match_score") p.sort = filters.sort;
    api.get("/marketplace/startups", { params: p })
      .then((r) => setStartups(r.data || []))
      .catch(() => setStartups([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null || value === undefined
        || (Array.isArray(value) && value.length === 0)
        || value === false) {
      next.delete(key);
    } else {
      next.set(key, Array.isArray(value) ? value.join(",") : String(value));
    }
    setParams(next, { replace: true });
  };

  const toggleInArray = (key, val) => {
    const arr = csv(params.get(key));
    const next = arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
    setParam(key, next);
  };

  const clearAll = () => setParams({}, { replace: true });

  const featured = startups.filter((s) => s.founder_premium);
  const rest = startups.filter((s) => !s.founder_premium);
  const activeCount = countActive(filters);
  const isDiversifyMode = filters.sectors_exclude.length > 0;

  // "Recently added by VenturaZ" — force-visibility toggled ON within last 7d.
  // Also matches pin/boost/introduce overrides via admin_introduced flag.
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000);
  const handpickedRecent = startups.filter((s) => {
    if (!s.admin_force_visible_at && !s.handpicked_by_venturaz) return false;
    const t = s.admin_force_visible_at ? new Date(s.admin_force_visible_at) : null;
    return (t && t >= sevenDaysAgo) || s.handpicked_by_venturaz;
  });
  const onlyRecent = params.get("filter") === "handpicked_recent";
  // "Show unlisted results" — when a search returns unlisted startups, offer
  // a toggle so the investor can hide/show them (default: show).
  const hasSearch = !!(filters.search && String(filters.search).trim());
  const unlistedCount = hasSearch ? startups.filter((s) => s.is_listed === false).length : 0;
  const hideUnlisted = params.get("hide_unlisted") === "1";
  const displayFeatured = onlyRecent ? featured.filter((s) => handpickedRecent.includes(s)) : featured;
  let displayRest = onlyRecent ? rest.filter((s) => handpickedRecent.includes(s)) : rest;
  if (hasSearch && hideUnlisted) displayRest = displayRest.filter((s) => s.is_listed !== false);

  const express = async (startup) => {
    try {
      await api.post("/investor/interest", { startup_id: startup.startup_id });
      toast.success("Interest expressed. Admin notified.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async (startup) => {
    try { await api.post(`/investor/saved/${startup.startup_id}`); toast.success("Saved."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Curated Deals" subtitle="INVESTOR · HANDPICKED OPPORTUNITIES">
      <Toaster richColors position="top-right" />

      {/* "Recently added by VenturaZ" top strip — surfaces this week's handpicks. */}
      {handpickedRecent.length > 0 && (
        <div
          className="mb-4 rounded-md border border-[#FFD700]/40 bg-gradient-to-r from-[#FAF6EC] via-[#F5EEDA] to-[#FAF6EC] p-3"
          data-testid="handpicked-strip"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0F3D2E] text-[#FFD700]">
                <Sparkles size={14} />
              </span>
              <div className="min-w-0">
                <div className="font-heading text-ink text-sm">Recently added by VenturaZ</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-soft">
                  {handpickedRecent.length} handpicked · last 7 days
                </div>
              </div>
            </div>
            <button
              onClick={() => setParam("filter", onlyRecent ? "" : "handpicked_recent")}
              className={`text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-md border transition-colors ${
                onlyRecent
                  ? "border-[#8A6600] bg-[#0F3D2E] text-[#FFD700]"
                  : "border-[#8A6600]/40 text-[#8A6600] hover:bg-[#FAF6EC]"
              }`}
              data-testid="handpicked-filter-toggle"
            >
              {onlyRecent ? "Showing handpicks only ✓" : "Show only these"}
            </button>
          </div>
        </div>
      )}

      {/* Unlisted-results toggle — surfaces only when a search brought back non-listed startups. */}
      {hasSearch && unlistedCount > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 border border-navy/10 rounded-md px-3 py-2 bg-white/60" data-testid="unlisted-toggle-row">
          <div className="text-xs text-ink-soft">
            <strong className="text-ink">{unlistedCount}</strong> unlisted result{unlistedCount === 1 ? "" : "s"} for "<span className="font-mono">{filters.search}</span>" — founders who haven't been listed on Curated Deals yet.
          </div>
          <button
            onClick={() => setParam("hide_unlisted", hideUnlisted ? "" : "1")}
            className={`text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${
              hideUnlisted
                ? "border-[#8A6600] text-[#8A6600] bg-gold/10 hover:bg-gold/20"
                : "border-navy/20 text-ink-soft hover:border-navy/40"
            }`}
            data-testid="unlisted-toggle-btn"
          >
            {hideUnlisted ? "Show unlisted" : "Hide unlisted"}
          </button>
        </div>
      )}

      {isDiversifyMode && (
        <div
          className="mb-4 border border-amber-300 bg-amber-50/60 rounded-md p-3 flex items-start justify-between gap-3"
          data-testid="marketplace-diversify-banner"
        >
          <div className="flex items-start gap-2">
            <Compass size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-amber-900 text-sm">Diversification view</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Hiding startups in <span className="font-heading">{filters.sectors_exclude.join(", ")}</span> to help you round out your portfolio.
              </div>
            </div>
          </div>
          <button
            onClick={() => setParam("sectors_exclude", [])}
            className="text-xs text-amber-900 hover:text-amber-950 font-heading underline flex-shrink-0"
            data-testid="marketplace-diversify-clear"
          >
            Show all sectors
          </button>
        </div>
      )}

      {/* Search + filter toggle bar */}
      <div className="border border-white/10 bg-white/70 backdrop-blur p-3 rounded-md mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex-1 flex items-center gap-2 min-w-[200px]">
          <Search size={16} className="text-ink-muted flex-shrink-0" />
          <input
            type="text"
            placeholder="Search startups"
            value={filters.search}
            onChange={(e) => setParam("search", e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-ink placeholder:text-ink-faint"
            data-testid="im-search"
          />
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-navy/15 text-ink text-xs font-heading hover:bg-navy/[0.04] transition-colors"
          data-testid="marketplace-open-filters"
        >
          <Filter size={12} /> Filters
          {activeCount > 0 && <Pill tone="gold">{activeCount}</Pill>}
        </button>
        <select
          value={filters.sort}
          onChange={(e) => setParam("sort", e.target.value === "match_score" ? "" : e.target.value)}
          className="text-xs font-heading border border-navy/15 rounded-md px-2 py-1.5 bg-white text-ink"
          data-testid="marketplace-sort"
          aria-label="Sort startups"
        >
          <option value="match_score">Sort · Match Score</option>
          <option value="newest">Sort · Newest</option>
          <option value="trust">Sort · Trust Score</option>
          <option value="ask_desc">Sort · Ask (High → Low)</option>
          <option value="ask_asc">Sort · Ask (Low → High)</option>
        </select>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 font-heading"
            data-testid="marketplace-clear-all"
          >
            <RotateCcw size={12} /> Clear
          </button>
        )}
      </div>

      {/* Active filter chips row */}
      {activeCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="marketplace-active-chips">
          {chipList(filters).map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest bg-emerald-50 border border-emerald-200 text-emerald-700"
              data-testid={`chip-${c.key}`}
            >
              {c.label}
              <button onClick={() => c.clear(setParam, params)} className="hover:text-rose-600" title="Remove">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {displayFeatured.length > 0 && (
        <div
          className="mb-6 border-2 border-gold/50 rounded-md bg-gradient-to-br from-navy/[0.03] via-gold/[0.03] to-white p-4"
          data-testid="featured-strip"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-gold" />
            <span className="overline text-gold">Founder Premium — Priority Review</span>
            <Pill tone="gold">{displayFeatured.length} featured</Pill>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {displayFeatured.map((s) => (
              <div
                key={s.startup_id}
                className="relative border border-gold/50 bg-white rounded-md ring-1 ring-gold/30"
                data-testid={`featured-card-${s.startup_id}`}
              >
                <div className="absolute -top-2 left-3 z-10">
                  <span className="inline-flex items-center gap-1 bg-gold text-navy text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded shadow">
                    <ShieldCheck size={10} /> Founder Premium
                  </span>
                </div>
                <StartupCard startup={s} onExpressInterest={express} />
                {s.priority_reason && (
                  <div className="px-4 pb-3 -mt-2 text-[11px] text-navy font-mono border-t border-gold/20 pt-2 italic">
                    <span className="uppercase tracking-wider text-[9px] text-gold not-italic">Priority match</span>
                    <div className="mt-1">{s.priority_reason}</div>
                  </div>
                )}
                <button
                  onClick={() => save(s)}
                  className="absolute top-4 right-4 text-xs text-ink-muted hover:text-gold"
                  data-testid={`save-${s.startup_id}`}
                >Save</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-ink-muted text-sm py-8 text-center">Loading marketplace…</div>
      ) : displayRest.length === 0 && displayFeatured.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-navy/15 rounded-md" data-testid="marketplace-empty">
          <div className="font-heading text-ink text-lg">
            {onlyRecent ? "No handpicks match your filters" : "No startups match your filters"}
          </div>
          <div className="text-sm text-ink-muted mt-1">
            {onlyRecent ? "Loosen filters or clear the handpicked filter." : "Loosen a filter or clear all to see the full marketplace."}
          </div>
          {(activeCount > 0 || onlyRecent) && (
            <button
              onClick={clearAll}
              className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading"
              data-testid="marketplace-empty-clear"
            >
              <RotateCcw size={12} /> Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="marketplace-grid">
          {displayRest.map((s) => (
            <div key={s.startup_id} className="relative">
              <StartupCard startup={s} onExpressInterest={express} />
              <button
                onClick={() => save(s)}
                className="absolute top-4 right-4 text-xs text-ink-muted hover:text-gold"
                data-testid={`save-${s.startup_id}`}
              >Save</button>
            </div>
          ))}
        </div>
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        facets={facets}
        setParam={setParam}
        toggleInArray={toggleInArray}
        clearAll={clearAll}
      />
    </DashboardShell>
  );
}

function FilterDrawer({ open, onClose, filters, facets, setParam, toggleInArray, clearAll }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex" data-testid="marketplace-filter-drawer">
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close filters"
      />
      <div className="w-full max-w-md bg-white shadow-2xl border-l border-navy/10 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-navy/10 px-5 py-3 flex items-center justify-between">
          <div className="font-heading text-ink flex items-center gap-2">
            <Filter size={16} className="text-emerald-600" /> Filters
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="marketplace-drawer-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <ChipGroup
            label="Sectors"
            options={facets.sectors}
            selected={filters.sectors}
            onToggle={(v) => toggleInArray("sectors", v)}
            testPrefix="filter-sector"
          />
          <ChipGroup
            label="Exclude sectors (diversify)"
            options={facets.sectors}
            selected={filters.sectors_exclude}
            onToggle={(v) => toggleInArray("sectors_exclude", v)}
            testPrefix="filter-sector-exclude"
            tone="rose"
          />
          <ChipGroup
            label="Stages"
            options={facets.stages}
            selected={filters.stages}
            onToggle={(v) => toggleInArray("stages", v)}
            testPrefix="filter-stage"
          />
          <ChipGroup
            label="Funding types"
            options={facets.funding_types}
            selected={filters.funding_types}
            onToggle={(v) => toggleInArray("funding_types", v)}
            testPrefix="filter-fundingtype"
          />

          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">City</div>
            <select
              value={filters.city}
              onChange={(e) => setParam("city", e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-navy/15 bg-white text-sm text-ink"
              data-testid="filter-city"
            >
              <option value="">Any city</option>
              {facets.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <RangeInput
              label="Min ask (₹)" value={filters.min_ask}
              onChange={(v) => setParam("min_ask", v)}
              testId="filter-min-ask" placeholder="e.g. 2500000"
            />
            <RangeInput
              label="Max ask (₹)" value={filters.max_ask}
              onChange={(v) => setParam("max_ask", v)}
              testId="filter-max-ask" placeholder="e.g. 100000000"
            />
            <RangeInput
              label="Min trust score" value={filters.min_trust}
              onChange={(v) => setParam("min_trust", v)}
              testId="filter-min-trust" placeholder="0-100"
              max={100}
            />
            <RangeInput
              label="Min completeness %" value={filters.min_completeness}
              onChange={(v) => setParam("min_completeness", v)}
              testId="filter-min-completeness" placeholder="0-100"
              max={100}
            />
          </div>

          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="filter-featured-wrap">
              <input
                type="checkbox"
                checked={filters.only_featured}
                onChange={(e) => setParam("only_featured", e.target.checked)}
                className="w-4 h-4 accent-emerald-600"
                data-testid="filter-only-featured"
              />
              <span className="text-sm text-ink">Only Founder Premium listings</span>
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-navy/10 pt-4 gap-3">
            <button
              onClick={clearAll}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-rose-600 text-xs font-heading hover:bg-rose-50"
              data-testid="marketplace-drawer-clear"
            >
              <RotateCcw size={12} /> Clear all
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading"
              data-testid="marketplace-drawer-apply"
            >
              Show results
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChipGroup({ label, options, selected, onToggle, testPrefix, tone = "emerald" }) {
  if (!options || options.length === 0) return null;
  const shown = options.slice(0, 30);
  const activeClass = tone === "rose"
    ? "border-rose-500 text-rose-700 bg-rose-50"
    : "border-emerald-600 text-emerald-700 bg-emerald-50";
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                on ? activeClass : "border-navy/15 text-ink-muted hover:border-emerald-300"
              }`}
              data-testid={`${testPrefix}-${opt}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangeInput({ label, value, onChange, testId, placeholder, max }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-md border border-navy/15 bg-white text-sm text-ink"
        data-testid={testId}
      />
    </div>
  );
}

/* ---------- helpers ---------- */

function csv(v) { return v ? v.split(",").filter(Boolean) : []; }

function countActive(f) {
  return (
    f.sectors.length + f.sectors_exclude.length + f.stages.length + f.funding_types.length +
    (f.city ? 1 : 0) + (f.min_ask ? 1 : 0) + (f.max_ask ? 1 : 0) +
    (f.min_trust ? 1 : 0) + (f.min_completeness ? 1 : 0) +
    (f.only_featured ? 1 : 0) + (f.search ? 1 : 0)
  );
}

function chipList(f) {
  const rows = [];
  const push = (key, label, clear) => rows.push({ key, label, clear });
  f.sectors.slice(0, CHIP_LIMIT).forEach((s) =>
    push(`sec-${s}`, `Sector: ${s}`, (setParam, params) => {
      const next = csv(params.get("sectors")).filter((x) => x !== s);
      setParam("sectors", next);
    })
  );
  f.sectors_exclude.slice(0, CHIP_LIMIT).forEach((s) =>
    push(`sxc-${s}`, `Exclude: ${s}`, (setParam, params) => {
      const next = csv(params.get("sectors_exclude")).filter((x) => x !== s);
      setParam("sectors_exclude", next);
    })
  );
  f.stages.slice(0, CHIP_LIMIT).forEach((s) =>
    push(`stg-${s}`, `Stage: ${s}`, (setParam, params) => {
      const next = csv(params.get("stages")).filter((x) => x !== s);
      setParam("stages", next);
    })
  );
  f.funding_types.slice(0, CHIP_LIMIT).forEach((s) =>
    push(`ft-${s}`, `Funding: ${s}`, (setParam, params) => {
      const next = csv(params.get("funding_types")).filter((x) => x !== s);
      setParam("funding_types", next);
    })
  );
  if (f.city) push("city", `City: ${f.city}`, (setParam) => setParam("city", ""));
  if (f.min_ask) push("min_ask", `Ask ≥ ₹${Number(f.min_ask).toLocaleString("en-IN")}`, (setParam) => setParam("min_ask", ""));
  if (f.max_ask) push("max_ask", `Ask ≤ ₹${Number(f.max_ask).toLocaleString("en-IN")}`, (setParam) => setParam("max_ask", ""));
  if (f.min_trust) push("min_trust", `Trust ≥ ${f.min_trust}`, (setParam) => setParam("min_trust", ""));
  if (f.min_completeness) push("min_completeness", `Completeness ≥ ${f.min_completeness}%`, (setParam) => setParam("min_completeness", ""));
  if (f.only_featured) push("only_featured", "Premium only", (setParam) => setParam("only_featured", false));
  if (f.search) push("search", `“${f.search}”`, (setParam) => setParam("search", ""));
  return rows;
}
